import { Attachment, Checkbox, Space, withModalConditionalRender } from '@billy/design-system'
import React, { ChangeEvent, ReactElement, ReactNode, useCallback } from 'react'

import { useIsEInvoicingOnly } from '@features/bills/hooks/useIsEInvoicingOnly'

import { AttachmentPreviewModal as AttachmentPreviewModalComponent } from '../../../../elements/AttachmentPreviewModal'
import { AttachmentType } from '../../../../enums/attachmentType'
import { getAttachmentPreviewModalId } from '../../utils/getAttachmentPreviewModalId'
import { AttachmentTileContent } from './elements/AttachmentTileContent'
import { AttachmentTileImage } from './elements/AttachmentTileImage'
import * as Styled from './styles'

const AttachmentPreviewModal = withModalConditionalRender(AttachmentPreviewModalComponent)

interface AttachmentTileProps {
  attachment: Attachment
  footer?: ReactNode
  isSelectable?: boolean
  isSelected?: boolean
  isShoeboxReadOnly?: boolean
  onImageClick?: () => void
  onOpenDuplicate?: (attachmentId: string) => void
  onSelect?: (id: string, isSelected: boolean) => void
  onFileSelectFromPreview?: () => void
  thumbnailBadge?: ReactNode
}

export const AttachmentTile = ({
  attachment,
  footer,
  isSelectable,
  isSelected,
  isShoeboxReadOnly,
  onImageClick,
  onOpenDuplicate,
  onSelect,
  onFileSelectFromPreview,
  thumbnailBadge,
}: AttachmentTileProps): ReactElement => {
  const { file, id, type } = attachment
  const attachmentPreviewModalId = getAttachmentPreviewModalId(id)
  const isEInvoicingOnly = useIsEInvoicingOnly()
  isSelectable = isEInvoicingOnly ? true : isSelectable
  const shouldShowCheckbox = isSelectable ?? type !== AttachmentType.EDocument

  const handleCheckboxChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onSelect?.(id, event.target.checked)
    },
    [id, onSelect],
  )

  const handleFileSelectFromPreview = useCallback(() => {
    onFileSelectFromPreview?.()
  }, [onFileSelectFromPreview])

  return (
    <>
      <Styled.AttachmentTileWrapper checked={isSelected}>
        {shouldShowCheckbox && (
          <Styled.AttachmentTileCheckboxWrapper>
            <Checkbox checked={isSelected} onChange={handleCheckboxChange} />
          </Styled.AttachmentTileCheckboxWrapper>
        )}
        <AttachmentTileImage
          attachmentImageFile={file}
          attachmentPreviewModalId={attachmentPreviewModalId}
          onClick={onImageClick}
          thumbnailBadge={thumbnailBadge}
        />
        <Styled.AttachmentTileContentWrapper>
          <AttachmentTileContent attachment={attachment} isShoeboxReadOnly={isShoeboxReadOnly} />
          {footer && (
            <>
              <Space size="s8" />
              {footer}
            </>
          )}
        </Styled.AttachmentTileContentWrapper>
      </Styled.AttachmentTileWrapper>

      <AttachmentPreviewModal
        id={attachmentPreviewModalId}
        attachment={attachment}
        isShoeboxReadOnly={isShoeboxReadOnly}
        onFileSelect={handleFileSelectFromPreview}
        onOpenDuplicate={onOpenDuplicate}
      />
    </>
  )
}
